import { Link } from "react-router-dom";
import { ShieldCheck, Landmark, GraduationCap, TrendingUp, Globe2, Briefcase, MessagesSquare, Clock, FileCheck, Users } from "lucide-react";
import PageHero from "@/components/PageHero";
import { ImageBand } from "@/components/visuals";
import CompareLedger from "@/components/CompareLedger";
import { Reveal } from "@/components/motion";
import { SectionHeading } from "@/components/shared";
import { WHY_EGOS, VS_TRADITIONAL } from "@/data/whyEgos";
import { SCHOOL } from "@/data/content";

function WhyGrid() {
  return (
    <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="why-egos-grid-section">
      <SectionHeading
        eyebrow="The Advantage"
        title={<>Why you should study at <span className="font-serif italic font-medium text-brand">EGOS</span></>}
        desc={<>Ten reasons students and families pick <strong className="font-bold text-ink">Eletech Global Ontario School</strong> over everything else — from ministry inspection to marking speed.</>}
      />
      <div className="mt-16 space-y-16 sm:space-y-24">
        {WHY_EGOS.map((w, i) => (
          <Reveal key={w.n}>
            <div className={`flex flex-col items-center gap-8 md:mx-auto md:max-w-5xl md:items-stretch md:gap-14 lg:gap-20 ${i % 2 ? "md:flex-row-reverse" : "md:flex-row"}`} data-testid={`why-egos-card-${w.n}`}>
              <div className="w-full flex-1">
                <p className="eyebrow">Reason {w.n}</p>
                <h3 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-ink">{w.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-slate-600">{w.text}</p>
              </div>
              <div className="relative w-full flex-1">
                <img
                  src={w.img}
                  alt={w.alt}
                  loading="lazy"
                  className="h-64 w-full rounded-3xl border border-slate-200 object-cover shadow-lg shadow-ink/5 sm:h-72"
                  data-testid={`why-egos-image-${w.n}`}
                />
                <span className="absolute -top-4 right-6 flex h-10 w-10 items-center justify-center rounded-full bg-brand font-mono text-sm font-bold text-white shadow-lg shadow-brand/30" data-testid={`why-egos-badge-${w.n}`}>
                  {w.n}
                </span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CompareSection({ id, eyebrow, title, desc, data, dark = false, testid }) {
  return (
    <section
      id={id}
      className={`py-20 sm:py-28 lg:py-32 ${dark ? "bg-ink" : "border-y border-slate-200/70 bg-sand/50"}`}
      data-testid={testid}
    >
      <div className="container-x">
        <SectionHeading dark={dark} eyebrow={eyebrow} title={title} desc={desc} />
        <div className={`mt-14 rounded-3xl p-2 sm:p-6 ${dark ? "bg-ink2/60 border border-ivory/10" : "bg-white/60"}`}>
          <CompareLedger data={data} testid={testid} />
        </div>
      </div>
    </section>
  );
}

const MINISTRY_IMG =
  "https://images.unsplash.com/photo-1761492190275-129cf71ff124?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function AboutIntro() {
  const facts = [
    { label: "Private & inspected", note: "Authorized by the Ontario Ministry of Education to grant OSSD credits" },
    { label: "On the Ministry's list", note: "Listed as an inspected private school in Ontario — look us up in the Ministry's private school database" },
    { label: "74 OSSD courses", note: "Grades 9 to 12, open every day of the year" },
    { label: "OCT teachers only", note: "Ontario Certified Teachers design and mark every course" },
  ];
  return (
    <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="about-egos-section">
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <SectionHeading
            eyebrow="About EGOS"
            title={<>A reputed Ontario school — <span className="font-serif italic font-medium text-brand">without the school bell</span></>}
            desc={<><strong className="font-bold text-ink">Eletech Global Ontario School</strong> is a private, ministry-inspected online high school. We grant the same OSSD and individual credits as any publicly inspected school in the province — on a timetable built around each student.</>}
          />
        </div>
        <div className="lg:col-span-6">
          <div className="grid gap-5 sm:grid-cols-2">
            {facts.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.06} className="h-full">
                <div className="card-line h-full p-6" data-testid={`about-fact-${i}`}>
                  <p className="font-display text-lg font-bold text-ink">{f.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{f.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function MinistrySection() {
  return (
    <section className="border-y border-slate-200/70 bg-sand/50 py-20 sm:py-28 lg:py-32" data-testid="ministry-section">
      <div className="container-x grid items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Reveal>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-3xl border-2 border-brand" />
              <img src={MINISTRY_IMG} alt="Modern Ontario school architecture" className="relative rounded-3xl object-cover shadow-2xl shadow-ink/20 h-[380px] w-full" data-testid="ministry-image" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <SectionHeading
            eyebrow="Ministry Inspection"
            title={<>Inspected, authorized, <span className="font-serif italic font-medium text-brand">recognized</span></>}
            desc="Private schools in Ontario that grant credits must pass a rigorous inspection by the Ministry of Education — covering curriculum delivery, assessment, teacher certification and record keeping."
          />
          <div className="mt-8 space-y-4">
            {[
              { icon: ShieldCheck, text: `Inspected and authorized by the Ontario Ministry of Education.` },
              { icon: Landmark, text: "Our credits appear on the official Ontario Student Transcript and are accepted by universities and colleges across Canada and worldwide." },
            ].map((row, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`ministry-credential-${i}`}>
                  <row.icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-aqua" />
                  <p className="text-sm leading-relaxed text-slate-700">{row.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const AUDIENCE = [
  { icon: GraduationCap, title: "Full-time OSSD students", text: "Families earning the whole diploma online — every credit from Grade 9 to graduation, on a schedule that fits the household rather than the other way around." },
  { icon: TrendingUp, title: "Mark upgraders", text: "Graduates and senior students retaking a course for a higher university average, or adding a missing prerequisite before a program starts." },
  { icon: Globe2, title: "International students", text: "Learners in any country collecting Canadian credits from home — no study permit needed until a university acceptance letter arrives." },
  { icon: Briefcase, title: "Athletes & adult learners", text: "Competitive training, careers and busy lives. Anyone who needs a real Ontario diploma without putting the rest of life on hold." },
];

function AudienceSection() {
  return (
    <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="who-for-section">
      <SectionHeading
        eyebrow="Who Studies With Us"
        title={<>Built for every kind of <span className="font-serif italic font-medium text-brand">ambitious student</span></>}
        desc="Different starting points, one diploma. See where you fit."
      />
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {AUDIENCE.map((a, i) => (
          <Reveal key={a.title} delay={i * 0.07} className="h-full">
            <div className="card-line h-full p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`audience-card-${i}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ink text-brandlight">
                <a.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-display text-base font-bold text-ink">{a.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{a.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const SUPPORT = [
  { icon: MessagesSquare, title: "Guidance that plans with you", text: "A guidance team maps your course path, checks program prerequisites and walks your university applications with you — included, never extra." },
  { icon: Clock, title: "Help when it actually happens", text: "Tutoring and academic support available around the clock, in any timezone. A student doesn't have to wait for tomorrow's class." },
  { icon: FileCheck, title: "Feedback on every task", text: "Ontario Certified Teachers mark quickly and personally, so the next attempt is smarter — not just scored." },
  { icon: Users, title: "Parents kept in the loop", text: "Families see real progress as it happens, with early intervention the moment momentum slips." },
];

function SupportSection() {
  return (
    <section className="border-y border-slate-200/70 bg-sand/50 py-20 sm:py-28 lg:py-32" data-testid="support-section">
      <div className="container-x">
        <SectionHeading
          eyebrow="Support That Shows Up"
          title={<>A whole team behind <span className="font-serif italic font-medium text-brand">every credit</span></>}
          desc="Online never means on-your-own. This is the human side of the school."
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SUPPORT.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.07} className="h-full">
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`support-card-${i}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ink text-brandlight">
                  <s.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-display text-base font-bold text-ink">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function MissionBand() {
  return (
    <section className="container-x py-20 sm:py-28" data-testid="mission-section">
      <Reveal>
        <div className="mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-10 text-center sm:p-14">
          <p className="eyebrow">Our Mission</p>
          <p className="mt-5 font-display text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            A reputed Ontario high school diploma for every student —{" "}
            <span className="font-serif italic font-medium text-brand">anywhere in the world, on any schedule.</span>
          </p>
        </div>
      </Reveal>
    </section>
  );
}

export default function WhyEgos() {
  return (
    <>
      <PageHero
        eyebrow="The EGOS Advantage"
        lines={[<>Ten reasons to study at EGOS —</>, <span className="font-serif italic font-medium text-brand">and one honest comparison</span>]}
        desc="See exactly how an EGOS education stacks up against a traditional bricks-and-mortar school — similar curriculum, similar certified teachers, a fundamentally different school day."
        testid="why-egos-page-heading"
      />

      <AboutIntro />

      <MinistrySection />

      <WhyGrid />

      <ImageBand
        src="https://images.pexels.com/photos/6958509/pexels-photo-6958509.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
        alt="A focused student in headphones takes notes beside a laptop during online study"
        tag="Focus, on your terms"
        testid="why-egos-image-band"
      />

      <AudienceSection />

      <CompareSection
        id="online-vs-traditional"
        eyebrow="The Choice for Parents"
        title={<>Online at EGOS <span className="font-serif italic font-medium text-brand">vs.</span> Traditional Brick &amp; Mortar School</>}
        desc="Similar curriculum, similar certified teachers, same diploma — a fundamentally different daily experience. Here is the point-by-point difference."
        data={VS_TRADITIONAL}
        dark
        testid="compare-traditional"
      />

      <SupportSection />

      <MissionBand />

      <section className="container-x pb-24" data-testid="why-egos-cta-section">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-brand px-8 py-14 text-center sm:px-16">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-white/80">Because we care</p>
            <h2 className="mx-auto mt-4 max-w-2xl font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
              Every advantage on this page is included in one flat per-credit fee.
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/admissions" data-testid="why-egos-cta-apply" className="btn-primary !bg-ink hover:!bg-ink2">
                Apply in <span className="text-brand">5 minutes</span>
              </Link>
              <Link to="/courses" data-testid="why-egos-cta-courses" className="btn-navy-outline !border-white/40 hover:!bg-white/10">
                Browse the <span className="text-ink">catalog</span>
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
